import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ThemeToggle } from "../sirius/ThemeProvider";
import { LanguageToggle, useT } from "../i18n";
import { log } from "../lib/log";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATE_CONFIG = {
  trialing:  { color: "var(--srs-sky)",     badgeKey: "account.badge.trialing",  badgeNb: "PRØVEPERIODE",        badgeEn: "TRIAL",            urgency: "calm" },
  active:    { color: "var(--srs-good)",    badgeKey: "account.badge.active",    badgeNb: "AKTIVT",              badgeEn: "ACTIVE",           urgency: "calm" },
  granted:   { color: "var(--srs-sun)",     badgeKey: "account.badge.granted",   badgeNb: "GRATIS — EUROPILOT",  badgeEn: "FREE — EUROPILOT", urgency: "calm" },
  past_due:  { color: "var(--srs-warn)",    badgeKey: "account.badge.past_due",  badgeNb: "FORFALT BETALING",    badgeEn: "PAST DUE",         urgency: "warn" },
  canceled:  { color: "var(--srs-ink-dim)", badgeKey: "account.badge.canceled",  badgeNb: "AVBRUTT",             badgeEn: "CANCELED",         urgency: "warn" },
  expired:   { color: "var(--srs-warn)",    badgeKey: "account.badge.expired",   badgeNb: "UTLØPT",              badgeEn: "EXPIRED",          urgency: "warn" },
  admin:     { color: "var(--srs-ink)",     badgeKey: "account.badge.admin",     badgeNb: "ADMIN",               badgeEn: "ADMIN",            urgency: "calm" },
};

export default function Account() {
  const { user, token, logout, refreshUser } = useAuth();
  const { t, lang } = useT();
  const [access, setAccess] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");
  const [verifyErr, setVerifyErr] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const isWelcome = params.get("welcome") === "1";
  const sessionId = params.get("session_id");
  // Cadence is set from the landing pricing toggle and carried through signup.
  // Defaults to "monthly" so existing flows behave identically.
  const urlCadence = (params.get("cadence") || "monthly").toLowerCase();
  const [cadence, setCadence] = useState(urlCadence === "yearly" ? "yearly" : "monthly");

  useEffect(() => {
    if (!token) {
      navigate("/login?next=/account");
      return;
    }
  }, [token, navigate]);

  // Fetch current access info; poll if returning from Stripe
  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;
    async function loadOnce() {
      try {
        // If we just returned from Stripe with session_id, sync first
        if (sessionId) {
          await fetch(`${BACKEND_URL}/api/stripe/checkout-status/${sessionId}`, {
            headers: { "Authorization": `Bearer ${token}` },
          });
        }
        const r = await fetch(`${BACKEND_URL}/api/auth/me-consumer`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (cancelled) return;
        if (r.ok) {
          const d = await r.json();
          setAccess(d.user.access);
        }
        // Fetch referral info in parallel
        const refR = await fetch(`${BACKEND_URL}/api/referrals/me`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (refR.ok && !cancelled) setReferrals(await refR.json());
      } catch (e) {
        log.warn("[account] referral load failed:", e?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (token) loadOnce();

    // If user just returned from Stripe checkout, poll a couple times for the webhook
    if (sessionId && token) {
      let attempts = 0;
      pollTimer = setInterval(async () => {
        attempts++;
        await loadOnce();
        if (attempts >= 4) clearInterval(pollTimer);
      }, 2500);
    }
    return () => { cancelled = true; if (pollTimer) clearInterval(pollTimer); };
  }, [token, sessionId]);

  async function startCheckout() {
    setActionBusy(true);
    setErr("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/stripe/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ origin: window.location.origin, cadence }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Stripe failed");
      window.location.href = d.url;
    } catch (e) {
      setErr(e.message);
    } finally {
      setActionBusy(false);
    }
  }

  async function openPortal() {
    setActionBusy(true);
    setErr("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/stripe/customer-portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ origin: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Portal failed");
      window.location.href = d.url;
    } catch (e) {
      setErr(e.message);
    } finally {
      setActionBusy(false);
    }
  }

  async function submitVerify() {
    if (verifyCode.length !== 6) return;
    setVerifyBusy(true);
    setVerifyErr("");
    setVerifyMsg("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ code: verifyCode }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Feil kode");
      if (d.user) setAccess(d.user.access);
      setVerifyMsg("E-postadressen er bekreftet ✓");
      setVerifyCode("");
    } catch (e) { setVerifyErr(e.message); }
    finally { setVerifyBusy(false); }
  }

  async function resendVerify() {
    setVerifyBusy(true);
    setVerifyErr("");
    setVerifyMsg("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Kunne ikke sende");
      setVerifyMsg("Ny kode sendt — sjekk e-posten din.");
    } catch (e) { setVerifyErr(e.message); }
    finally { setVerifyBusy(false); }
  }

  if (!token) return null;
  if (loading || !user || !access) {
    return (
      <div className="srs" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }} data-testid="account-loading">
        <div className="srs-mono" style={{ letterSpacing: "0.3em", color: "var(--srs-ink-dim)" }}>LASTER…</div>
      </div>
    );
  }

  const cfg = STATE_CONFIG[access.state] || STATE_CONFIG.expired;
  const hasStripe = access.stripe_customer_id;
  const needsCard = access.state === "trialing" && !hasStripe;
  const needsPayment = ["expired", "past_due", "canceled"].includes(access.state);

  return (
    <div className="srs" style={{ minHeight: "100vh" }} data-testid="account-root">
      {/* nav */}
      <nav style={navBar}>
        <Link to="/" style={brandLink} data-testid="account-brand">
          <span className="srs-logo-dot" />
          <span className="srs-display" style={{ fontSize: 24, fontWeight: 500 }}>EuroPilot</span>
        </Link>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <LanguageToggle />
          <ThemeToggle />
          <Link to="/account/support" style={ghostBtnSm} data-testid="account-support-link">{t("account.nav.support")}</Link>
          <Link to="/faq" style={ghostBtnSm} data-testid="account-faq-link">{t("account.nav.faq")}</Link>
          {user.role === "owner" || user.role === "admin" ? (
            <Link to="/amex" style={ghostBtnSm} data-testid="account-admin-link">{t("account.nav.admin")}</Link>
          ) : null}
          <button onClick={logout} style={ghostBtnSm} data-testid="account-logout">{t("account.nav.logout")}</button>
        </div>
      </nav>

      <main style={{ maxWidth: 920, margin: "0 auto", padding: "40px 28px 80px" }}>
        {isWelcome && access.state === "trialing" && (
          <div style={{ marginBottom: 24, padding: "14px 18px", background: "var(--srs-good-soft)", border: "1px solid var(--srs-good)", borderRadius: 14, color: "var(--srs-good)", fontSize: 13, fontWeight: 600 }} data-testid="welcome-banner">
            {t("account.welcome.title")}
          </div>
        )}

        {!access.email_verified && (
          <div className="srs-card" style={{ marginBottom: 24, padding: "20px 24px", borderRadius: 16, border: "1px solid var(--srs-warn)", background: "color-mix(in srgb, var(--srs-warn) 8%, var(--srs-surface))" }} data-testid="verify-card">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--srs-warn)", fontWeight: 700 }}>{t("account.verify.eyebrow")}</div>
            <div style={{ marginTop: 8, fontSize: 14, color: "var(--srs-ink)", lineHeight: 1.55 }}>
              {(() => {
                // Safe substitution: split the translated string at the
                // {email} placeholder and inject the email as a JSX <strong>.
                // Avoids dangerouslySetInnerHTML so a malformed email can't
                // ever inject HTML/JS.
                const parts = t("account.verify.body").split("{email}");
                return (
                  <>
                    {parts[0]}
                    <strong>{user.email}</strong>
                    {parts.slice(1).join("{email}")}
                  </>
                );
              })()}
            </div>
            <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                data-testid="verify-code-input"
                style={{ width: 140, padding: "12px 14px", background: "var(--srs-surface)", border: "1px solid var(--srs-line)", borderRadius: 10, fontSize: 18, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.3em", textAlign: "center", color: "var(--srs-ink)", outline: "none" }}
              />
              <button onClick={submitVerify} disabled={verifyBusy || verifyCode.length !== 6} className="srs-cta" style={{ padding: "12px 22px" }} data-testid="verify-submit">
                {verifyBusy ? t("account.verify.submit_busy") : t("account.verify.submit")}
              </button>
              <button onClick={resendVerify} disabled={verifyBusy} className="srs-cta-ghost" style={{ padding: "12px 18px" }} data-testid="verify-resend">
                {t("account.verify.resend")}
              </button>
            </div>
            {verifyErr && <div style={{ marginTop: 10, fontSize: 12, color: "var(--srs-warn)" }} data-testid="verify-error">{verifyErr}</div>}
            {verifyMsg && <div style={{ marginTop: 10, fontSize: 12, color: "var(--srs-good)" }} data-testid="verify-msg">{verifyMsg}</div>}
          </div>
        )}

        <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-dim)" }}>{t("account.eyebrow")}</div>
        <h1 className="srs-display" style={{ fontSize: "clamp(40px, 5vw, 64px)", letterSpacing: "-0.02em", margin: "10px 0 4px", lineHeight: 1.05 }}>
          {user.name || user.email.split("@")[0]}
        </h1>
        <div style={{ fontSize: 14, color: "var(--srs-ink-dim)" }}>{user.email}</div>

        {/* Status card */}
        <div className="srs-card" style={{ marginTop: 32, padding: "36px 36px", borderRadius: 24, background: "var(--srs-hero-gradient)" }} data-testid="status-card">
          <span style={{ display: "inline-block", padding: "5px 12px", fontSize: 10, letterSpacing: "0.25em", fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", background: cfg.color, color: "var(--srs-surface)", borderRadius: 999 }} data-testid={`status-badge-${access.state}`}>
            {lang === "en" ? cfg.badgeEn : cfg.badgeNb}
          </span>
          <div className="srs-display" style={{ fontSize: 38, fontWeight: 500, marginTop: 16, lineHeight: 1.1 }}>
            {t(`account.state.${access.state}`)}
          </div>
          {access.days_left != null && (
            <div style={{ marginTop: 6, fontSize: 14, color: "var(--srs-ink-dim)" }}>
              {access.days_left} {access.days_left === 1 ? (lang === "en" ? "day" : "dag") : (lang === "en" ? "days" : "dager")} {lang === "en" ? "left" : "igjen"}
              {access.state === "granted" && (lang === "en" ? " (admin access)" : " (admin-tilgang)")}
            </div>
          )}

          <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" }}>
            {needsCard && (
              <button onClick={startCheckout} disabled={actionBusy} className="srs-cta" data-testid="add-card-btn">
                {actionBusy ? t("account.cta.busy") : t("account.cta.start_trial")}
              </button>
            )}
            {needsPayment && (
              <button onClick={startCheckout} disabled={actionBusy} className="srs-cta" data-testid="resume-btn">
                {actionBusy ? t("account.cta.busy") : t("account.cta.renew")}
              </button>
            )}
            {hasStripe && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button onClick={openPortal} disabled={actionBusy} className="srs-cta-ghost" data-testid="portal-btn">
                  {actionBusy ? "…" : t("account.cta.manage")}
                </button>
                <div style={{ fontSize: 11, color: "var(--srs-ink-faint)", maxWidth: 320, lineHeight: 1.4 }}>
                  {lang === "en" ? "Update your card, download receipts, or cancel — all via Stripe." : "Her kan du oppdatere kortet, laste ned kvitteringer eller si opp abonnementet."}
                </div>
              </div>
            )}
          </div>

          {err && <div style={{ marginTop: 16, color: "var(--srs-warn)", fontSize: 12 }} data-testid="action-error">{err}</div>}
        </div>

        {/* Details */}
        <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }} data-testid="details-grid">
          <DetailCard label={lang === "en" ? "TRIAL ENDS" : "PRØVEPERIODE SLUTTER"} value={fmtDate(access.trial_ends_at)} />
          <DetailCard label={lang === "en" ? "FREE ACCESS (EUROPILOT)" : "GRATIS TILGANG (EUROPILOT)"} value={fmtDate(access.granted_until) || "—"} />
          <DetailCard label={lang === "en" ? "NEXT CHARGE" : "NESTE BETALING"} value={fmtDate(access.current_period_end) || "—"} />
          <DetailCard label={lang === "en" ? "STRIPE STATUS" : "STRIPE-STATUS"} value={access.subscription_status || (lang === "en" ? "none" : "ingen")} />
        </div>

        <div style={{ marginTop: 32, padding: 24, border: "1px dashed var(--srs-line)", borderRadius: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div>
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--srs-ink-dim)" }}>{t("account.download.button")}</div>
            <div style={{ marginTop: 6, fontSize: 13, color: "var(--srs-ink-dim)" }}>{lang === "en" ? "Log in with the same email inside the extension to activate scanning." : "Logg inn med samme e-post inne i utvidelsen for å aktivere skanning."}</div>
          </div>
          <a href="https://chromewebstore.google.com/detail/europilot-%E2%80%94-sas-eurobonus/lficlephbfmbhmgcpchfnpfhdpponild" target="_blank" rel="noopener noreferrer" className="srs-cta" data-testid="install-cws">{lang === "en" ? "Get it on Chrome Web Store" : "Last ned i Chrome Web Store"}</a>
        </div>

        <TwoFactorSection />

        {referrals && (
          <div className="srs-card" style={{ marginTop: 24, padding: "36px 36px", borderRadius: 24 }} data-testid="referrals-card">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-sun)", marginBottom: 12 }}>// VERV VENNER</div>
            <h2 className="srs-display" style={{ fontSize: 32, fontWeight: 500, margin: 0, lineHeight: 1.1 }}>
              3 betalende venner = <em style={{ color: "var(--srs-sun)" }}>1 gratis måned</em>
            </h2>
            <p style={{ marginTop: 12, fontSize: 13, color: "var(--srs-ink-dim)", lineHeight: 1.55, maxWidth: 540 }}>
              Hver venn som registrerer seg med koden din får 14 dager gratis prøveperiode (i stedet for 7). Når 3 av dem fortsetter som betalende abonnenter, får du automatisk en gratis måned lagt til kontoen din.
            </p>

            <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }} className="ref-stats">
              <DetailCard label="DIN KODE" value={referrals.code} />
              <DetailCard label="BETALENDE VENNER" value={String(referrals.paid_referrals_count)} />
              <DetailCard label={`${referrals.needed_for_next_month} TIL NESTE MND`} value={referrals.months_earned ? `${referrals.months_earned} mnd tjent` : "Snart!"} />
            </div>

            <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 220, display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: "var(--srs-surface-2)", border: "1px solid var(--srs-line)", borderRadius: 12, fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, color: "var(--srs-ink)", overflow: "hidden" }}>
                <span style={{ color: "var(--srs-ink-faint)", fontSize: 11 }}>LINK:</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} data-testid="referral-link">
                  {window.location.origin}/signup?ref={referrals.code}
                </span>
              </div>
              <button onClick={async () => {
                try {
                  await navigator.clipboard.writeText(`${window.location.origin}/signup?ref=${referrals.code}`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch (e) {
                  log.warn("[account] clipboard copy failed:", e?.message);
                }
              }} className="srs-cta-ghost" style={{ padding: "12px 18px" }} data-testid="copy-referral-link">
                {copied ? t("account.referrals.copied") : t("account.referrals.copy")}
              </button>
            </div>

            {referrals.referred?.length > 0 && (
              <div style={{ marginTop: 28 }}>
                <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--srs-ink-dim)", marginBottom: 10 }}>VERVEDE</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {referrals.referred.map((r) => {
                    const status = r.subscription_status;
                    const credited = r.referral_credited;
                    const tag = credited ? { label: "TELLER MED", color: "var(--srs-good)" }
                              : status === "trialing" ? { label: "I PRØVEPERIODE", color: "var(--srs-sky)" }
                              : status === "active" ? { label: "AKTIV", color: "var(--srs-good)" }
                              : { label: "VENTER PÅ BETALING", color: "var(--srs-ink-dim)" };
                    return (
                      <div key={r.email} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "var(--srs-surface-2)", borderRadius: 10, border: "1px solid var(--srs-line)", fontSize: 13 }}>
                        <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: "var(--srs-ink-dim)" }}>{r.email}</span>
                        <span className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.18em", color: tag.color, fontWeight: 600 }}>{tag.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function DetailCard({ label, value }) {
  return (
    <div style={{ padding: 18, background: "var(--srs-surface)", border: "1px solid var(--srs-line)", borderRadius: 14 }}>
      <div className="srs-mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: "var(--srs-ink-dim)" }}>{label}</div>
      <div style={{ marginTop: 8, fontSize: 14, color: "var(--srs-ink)", fontWeight: 600 }}>{value || "—"}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("nb-NO", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

function TwoFactorSection() {
  const { apiFetch } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await apiFetch("/api/auth/2fa/status");
        if (!cancelled) setStatus(d);
      } catch (e) {
        log.warn("[account] 2fa/status failed:", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [apiFetch]);

  const toggle = async () => {
    setBusy(true); setMsg("");
    try {
      const path = status?.two_factor_enabled ? "/api/auth/2fa/disable" : "/api/auth/2fa/enable";
      const d = await apiFetch(path, { method: "POST" });
      setStatus((s) => ({ ...(s || {}), two_factor_enabled: d.two_factor_enabled, trusted_device_count: 0 }));
      setMsg(d.two_factor_enabled
        ? "To-faktor er aktivert. Neste innlogging må du skrive inn en kode fra e-posten din."
        : "To-faktor er deaktivert. Du logger inn med bare passord nå.");
    } catch (e) {
      setMsg(`Feil: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const on = !!status.two_factor_enabled;
  const forced = !!status.globally_required;

  return (
    <div className="srs-card" style={{ marginTop: 24, padding: "28px 32px", borderRadius: 20 }} data-testid="twofa-section">
      <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-dim)", marginBottom: 10 }}>// SIKKERHET</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h2 className="srs-display" style={{ fontSize: 22, fontWeight: 500, margin: "0 0 6px 0" }}>
            To-faktor-autentisering
          </h2>
          <p style={{ fontSize: 13, color: "var(--srs-ink-dim)", lineHeight: 1.55, margin: 0 }}>
            Vi sender deg en 6-sifret kode på e-post hver gang du logger inn på nettsiden.
            I Chrome-utvidelsen trenger du bare å skrive inn koden første gang du logger inn — etterpå husker utvidelsen enheten din.
          </p>
          {forced && (
            <div style={{ marginTop: 12, fontSize: 12, color: "var(--srs-sun)", fontWeight: 600 }}>
              Påkrevd: EuroPilot har slått på 2FA for alle brukere.
            </div>
          )}
          {msg && (
            <div style={{ marginTop: 12, fontSize: 13, color: msg.startsWith("Feil") ? "var(--srs-warn)" : "var(--srs-good)", lineHeight: 1.5 }} data-testid="twofa-msg">{msg}</div>
          )}
        </div>
        <button
          onClick={toggle}
          disabled={busy || forced}
          className={on ? "srs-cta-ghost" : "srs-cta"}
          style={{ minWidth: 160, justifyContent: "center" }}
          data-testid={on ? "twofa-disable-btn" : "twofa-enable-btn"}
          title={forced ? "Globalt påkrevd av EuroPilot — kan ikke slås av" : ""}
        >
          {busy ? "…" : on ? "Slå av" : "Slå på 2FA →"}
        </button>
      </div>
    </div>
  );
}

const navBar = { position: "sticky", top: 0, zIndex: 30, background: "color-mix(in srgb, var(--srs-bg) 88%, transparent)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--srs-line)", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" };
const brandLink = { display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "var(--srs-ink)" };
const ghostBtnSm = { background: "transparent", color: "var(--srs-ink-dim)", border: "1px solid var(--srs-line)", padding: "8px 14px", borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: "pointer", textDecoration: "none" };
